import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../scss/invoice-footer.scss';
import SendInvoiceConfirmationModal from './send-invoice-confirmation-modal.component';
import BankInfoModal from './bank-info-modal.component';
import { SaveInvoiceModal } from './save-invoice-modal.component';
import { formatAmountWithLocale } from '../helper/common.helper';
import apiCall from '../../api';

interface FreelancerSubtotal {
  name: string;
  subtotal: string;
}

interface InvoiceFooterProps {
  grandTotal?: string;
  onSendInvoice?: () => Promise<boolean> | void;
  isLoading?: boolean;
  isEditMode?: boolean;
  invoiceDay?: string;
  dueDay?: string;
  freelancerSubtotals?: FreelancerSubtotal[];
  calculatedSubAmount?: any;
  selectedCustomer?: any;
  validateServiceDescriptionsRef?: React.MutableRefObject<{[freelancerId: string]: () => boolean} | undefined>;
  dataRef?: React.MutableRefObject<any>;
   onPreview?: () => void;
  onSaveDraft?: () => void;
  isPreviewLoading?: boolean;
  getPayload?: () => any;
  onSecondApiCall?: (pdfBlob: Blob) => Promise<void>;
  dueDateFromPreferences?: string; // NEW - Due date from invoice preferences
}

export const InvoiceFooter: React.FC<InvoiceFooterProps> = ({
  grandTotal = "XXXX Kr",
  onSendInvoice,
  isLoading = false,
  isEditMode,
  invoiceDay = '',
  dueDay = '',
  freelancerSubtotals = [],
  calculatedSubAmount,
  selectedCustomer,
  validateServiceDescriptionsRef,
  dataRef,
  onPreview,
  onSaveDraft,
  isPreviewLoading = false,
  getPayload,
  onSecondApiCall,
  dueDateFromPreferences,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showBankInfoModal, setShowBankInfoModal] = useState(false);
   const [showSaveModal, setShowSaveModal] = useState(false);
  
  // Extract query parameters for warning condition
  const queryParams = new URLSearchParams(window.location.search);
  const customerIdFromQuery = parseInt(queryParams.get("customerId") || "", 10) || undefined;
  const preId = queryParams.get("preId") || undefined;

  // Helper function to check bank info
  const checkBankInfo = async (): Promise<boolean> => {
    try {
      const userDataString = localStorage.getItem('user');
      if (!userDataString) {
        toast.error(String(t("User data not found") || "User data not found"));
        return false;
      }

      const user = JSON.parse(userDataString);
      const userId = user?.userData?.userId;

      if (!userId) {
        toast.error(String(t("User ID not found") || "User ID not found"));
        return false;
      }

      const res = await apiCall.get(`/employee/get/${userId}`);
      const response = res?.data?.response;

      // Check if customerSalaryInformation is empty array
      if (response?.customerSalaryInformation?.length === 0) {
        setShowBankInfoModal(true);
        return false;
      }

      // Check if all bank info fields are empty
      if (
        !response?.customerSalaryInformation[0]?.accountNumber &&
        !response?.customerSalaryInformation[0]?.clearingNumber &&
        !response?.customerSalaryInformation[0]?.ibanNumber &&
        !response?.customerSalaryInformation[0]?.bicNumber
      ) {
        setShowBankInfoModal(true);
        return false;
      }

      // Check if at least one valid combination exists
      const hasDomesticBankInfo = 
        response?.customerSalaryInformation[0]?.accountNumber &&
        response?.customerSalaryInformation[0]?.clearingNumber;

      const hasInternationalBankInfo = 
        response?.customerSalaryInformation[0]?.ibanNumber &&
        response?.customerSalaryInformation[0]?.bicNumber;

      if (!hasDomesticBankInfo && !hasInternationalBankInfo) {
        setShowBankInfoModal(true);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error checking bank info:', err);
      toast.error(String(t("Error checking bank information") || "Error checking bank information"));
      return false;
    }
  };

  // Helper function to validate description
  const validateDescription = (): boolean => {
    const invoiceData = dataRef?.current;
    const description = invoiceData?.formData?.otherDetails?.customerNotes || "";
    if (!description || description.trim().length === 0) {
      toast.error(String(t("PLEASE_ENTER_DESCRIPTION_BEFORE_CREATING_INVOICE") || "Please enter customer notes (description) before creating invoice"));
      return false;
    }

    const descriptionLength = description.trim().length;
    const wordCount = description.trim().split(/\s+/).filter((word: string) => word.length > 0).length;
    
    if (descriptionLength < 20 && wordCount < 3) {
      toast.error(String(t("Enter at least 20 characters or 3 words") || "Enter at least 20 characters or 3 words in customer notes"));
      return false;
    }

    return true;
  };
  const handlePreviewClick = () => {
    // 1. Validate description first
    if (!validateDescription()) {
      return;
    }

    // 2. Validate service descriptions for all freelancers
    if (validateServiceDescriptionsRef?.current) {
      const validationFunctions = validateServiceDescriptionsRef.current;
      const freelancerIds = Object.keys(validationFunctions);

      for (const freelancerId of freelancerIds) {
        const validateFunc = validationFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t("PLEASE_FILL_SERVICE_DESCRIPTION") || "Please fill in service description for all working hours"));
          return;
        }
      }
    }

    // Only call parent's onPreview - let parent handle modal
    if (onPreview) {
      onPreview();
    }
  };

  const handleSaveDraftClick = () => {
    // 1. Validate description first
    if (!validateDescription()) {
      return;
    }

    // 2. Validate service descriptions for all freelancers
    if (validateServiceDescriptionsRef?.current) {
      const validationFunctions = validateServiceDescriptionsRef.current;
      const freelancerIds = Object.keys(validationFunctions);

      for (const freelancerId of freelancerIds) {
        const validateFunc = validationFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t("PLEASE_FILL_SERVICE_DESCRIPTION") || "Please fill in service description for all working hours"));
          return;
        }
      }
    }

    setShowSaveModal(true);
  };

  const handleConfirmSave = () => {
    if (onSaveDraft) {
      onSaveDraft();
    }
  };


  const handleButtonClick = async () => {
    // 1. Check bank info first
    const hasBankInfo = await checkBankInfo();
    if (!hasBankInfo) {
      return;
    }

    // 2. Validate minimum invoice amount (1000 SEK)
    const invoiceAmount = calculatedSubAmount?.response?.totalCalculation?.amount || 0;
    if (invoiceAmount < 1000) {
      toast.error(String(t("MINIMUM_INVOICE_AMOUNT_ERROR") || "Minimum 1000 SEK amount is required to create an invoice"));
      return;
    }

    // 3. Validate description
    if (!validateDescription()) {
      return;
    }

    // 4. Validate service descriptions for all freelancers
    if (validateServiceDescriptionsRef?.current) {
      const validationFunctions = validateServiceDescriptionsRef.current;
      const freelancerIds = Object.keys(validationFunctions);
      
      for (const freelancerId of freelancerIds) {
        const validateFunc = validationFunctions[freelancerId];
        if (validateFunc && !validateFunc()) {
          toast.error(String(t("PLEASE_FILL_SERVICE_DESCRIPTION") || "Please fill in service description for all working hours"));
          return;
        }
      }
    }

    // 5. If all validations pass, show confirmation modal
    setShowConfirmModal(true);
  };

  const handleConfirmSend = async () => {
    if (onSendInvoice) {
      const result = await onSendInvoice();
      // Return full result (could be boolean or API response object)
      // This allows the modal to extract isAlreadySynced if available
      return result;
    }
    return false;
  };

  const handleSuccess = () => {
    // Navigate after success modal is closed
    const currentLanguage = i18n.language || 'en-us';
    navigate(`/v2/manage-invoice?lang=${currentLanguage}`);
  };

  const handleCloseModal = () => {
    if (!isLoading) {
      setShowConfirmModal(false);
    }
  };

 
  return (
    <>
      <div className="invoice-footer">
        <div className="grand-total-section">
          <div className="grand-total-content">
            <span className="grand-total-label">{t('GRAND_TOTAL')}</span>
            <span className="grand-total-value">{formatAmountWithLocale(calculatedSubAmount?.response?.totalCalculation.amountIncludingVat || 0, calculatedSubAmount?.response?.totalCalculation.currencyName || 'SEK', i18n.language)}</span>
          </div>
        </div>
        
        <div className="footer-actions">
           <button 
            className="nav-btn preview-btn" 
            onClick={handlePreviewClick}
            disabled={isPreviewLoading}
            style={{ 
              opacity: isPreviewLoading ? 0.7 : 1,
              cursor: isPreviewLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isPreviewLoading ? (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="8" cy="8" r="6" stroke="#544196" strokeWidth="2" strokeDasharray="30" strokeLinecap="round" opacity="0.3"/>
                  <circle cx="8" cy="8" r="6" stroke="#544196" strokeWidth="2" strokeDasharray="10 20" strokeLinecap="round"/>
                </svg>
                {t('LOADING')}
              </>
            ) : (
              <>
                 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M4.66797 11.9566V4.03891C4.66797 2.93268 5.93743 2.3076 6.81425 2.98208L11.9608 6.94093C12.6546 7.47466 12.6546 8.52087 11.9608 9.05459L6.81425 13.0134C5.93742 13.6879 4.66797 13.0628 4.66797 11.9566Z" stroke="#544196" stroke-width="2" stroke-linecap="round"/>
</svg>
                {t('PREVIEW')}
              </>
            )}
          </button>
          {!isEditMode && (
          <button className="nav-btn save-draft-btn" onClick={handleSaveDraftClick}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M13.1893 2.06395L14.0803 1.60996L14.0803 1.60996L13.1893 2.06395ZM11.2013 1.33594L11.2013 2.33594L11.2013 2.33594L11.2013 1.33594ZM12.6066 1.48126L13.0606 0.590257L13.0606 0.590256L12.6066 1.48126ZM12.6066 14.5239L13.0606 15.415L13.0606 15.415L12.6066 14.5239ZM13.1893 13.9413L14.0803 14.3952L14.0803 14.3952L13.1893 13.9413ZM3.39598 14.5239L2.94199 15.415L2.94199 15.415L3.39598 14.5239ZM2.81329 13.9413L3.7043 13.4873L3.7043 13.4873L2.81329 13.9413ZM3.0585 5.61205L2.35139 4.90494L2.35139 4.90494L3.0585 5.61205ZM6.94411 1.72646L6.23701 1.01935L6.23701 1.01935L6.94411 1.72646ZM4.8013 14.6693V15.6693H11.2013V14.6693V13.6693H4.8013V14.6693ZM13.3346 12.5359H14.3346V3.46927H13.3346H12.3346V12.5359H13.3346ZM13.3346 3.46927H14.3346C14.3346 3.1124 14.3354 2.78461 14.3131 2.51211C14.2899 2.22793 14.2368 1.91704 14.0803 1.60996L13.1893 2.06395L12.2983 2.51794C12.2872 2.49608 12.3067 2.51448 12.3198 2.67497C12.3339 2.84714 12.3346 3.0794 12.3346 3.46927H13.3346ZM11.2013 1.33594L11.2013 2.33594C11.5912 2.33594 11.8234 2.33672 11.9956 2.35078C12.1561 2.36389 12.1745 2.38341 12.1526 2.37227L12.6066 1.48126L13.0606 0.590256C12.7535 0.433793 12.4426 0.380642 12.1585 0.357424C11.886 0.33516 11.5582 0.335937 11.2013 0.335938L11.2013 1.33594ZM13.1893 2.06395L14.0803 1.60996C13.8566 1.17091 13.4997 0.81396 13.0606 0.590257L12.6066 1.48126L12.1526 2.37227C12.2154 2.40423 12.2663 2.45522 12.2983 2.51794L13.1893 2.06395ZM11.2013 14.6693V15.6693C11.5582 15.6693 11.886 15.67 12.1585 15.6478C12.4426 15.6246 12.7535 15.5714 13.0606 15.415L12.6066 14.5239L12.1526 13.6329C12.1745 13.6218 12.1561 13.6413 11.9956 13.6544C11.8234 13.6685 11.5912 13.6693 11.2013 13.6693V14.6693ZM13.3346 12.5359H12.3346C12.3346 12.9258 12.3339 13.1581 12.3198 13.3302C12.3067 13.4907 12.2872 13.5091 12.2983 13.4873L13.1893 13.9413L14.0803 14.3952C14.2368 14.0882 14.2899 13.7773 14.3131 13.4931C14.3354 13.2206 14.3346 12.8928 14.3346 12.5359H13.3346ZM12.6066 14.5239L13.0606 15.415C13.4997 15.1912 13.8566 14.8343 14.0803 14.3952L13.1893 13.9413L12.2983 13.4873C12.2663 13.55 12.2154 13.601 12.1526 13.6329L12.6066 14.5239ZM4.8013 14.6693V13.6693C4.41144 13.6693 4.17918 13.6685 4.00701 13.6544C3.8465 13.6413 3.82811 13.6218 3.84997 13.6329L3.39598 14.5239L2.94199 15.415C3.24907 15.5714 3.55997 15.6246 3.84415 15.6478C4.11665 15.67 4.44444 15.6693 4.8013 15.6693V14.6693ZM2.66797 12.5359H1.66797C1.66797 12.8928 1.66719 13.2206 1.68946 13.4931C1.71267 13.7773 1.76582 14.0882 1.92229 14.3952L2.81329 13.9413L3.7043 13.4873C3.71544 13.5091 3.69593 13.4907 3.68281 13.3302C3.66875 13.1581 3.66797 12.9258 3.66797 12.5359H2.66797ZM3.39598 14.5239L3.84997 13.6329C3.78725 13.601 3.73626 13.55 3.7043 13.4873L2.81329 13.9413L1.92229 14.3952C2.14599 14.8343 2.50295 15.1912 2.94199 15.415L3.39598 14.5239ZM2.66797 6.55486H1.66797V12.5359H2.66797H3.66797V6.55486H2.66797ZM11.2013 1.33594V0.335938H7.88692V1.33594V2.33594H11.2013V1.33594ZM3.0585 5.61205L3.7656 6.31916L7.65122 2.43357L6.94411 1.72646L6.23701 1.01935L2.35139 4.90494L3.0585 5.61205ZM3.33464 6.0026V7.0026H6.61669V6.0026V5.0026H3.33464V6.0026ZM7.33464 5.28465H8.33464V2.0026H7.33464H6.33464V5.28465H7.33464ZM6.61669 6.0026V7.0026C7.56549 7.0026 8.33464 6.23344 8.33464 5.28465H7.33464H6.33464C6.33464 5.12889 6.46091 5.0026 6.61669 5.0026V6.0026ZM7.88692 1.33594V0.335938C7.55024 0.335938 7.22102 0.408722 6.92044 0.54551L7.33464 1.4557L7.74884 2.36588C7.79185 2.34631 7.83882 2.33594 7.88692 2.33594V1.33594ZM7.33464 1.4557L6.92044 0.54551C6.66849 0.660164 6.43651 0.819847 6.23701 1.01935L6.94411 1.72646L7.65122 2.43357C7.67974 2.40505 7.71282 2.38227 7.74884 2.36588L7.33464 1.4557ZM7.33464 2.0026H8.33464V1.4557H7.33464H6.33464V2.0026H7.33464ZM2.66797 6.55486H3.66797C3.66797 6.50677 3.67834 6.45979 3.69791 6.41679L2.78772 6.0026L1.87752 5.58842C1.74075 5.889 1.66797 6.2182 1.66797 6.55486H2.66797ZM2.78772 6.0026L3.69791 6.41679C3.7143 6.38077 3.73708 6.34768 3.7656 6.31916L3.0585 5.61205L2.35139 4.90494C2.15187 5.10446 1.99218 5.33646 1.87752 5.58842L2.78772 6.0026ZM3.33464 6.0026V5.0026H2.78772V6.0026V7.0026H3.33464V6.0026Z" fill="#544196"/>
</svg>
            {t('SAVE_DRAFT')}
          </button> )}
          <button 
            className="send-invoice-btn" 
            onClick={handleButtonClick}
            disabled={isLoading}
            style={{ 
              opacity: isLoading ? 0.7 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                  <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="2" strokeDasharray="30" strokeLinecap="round" opacity="0.3"/>
                  <circle cx="8" cy="8" r="6" stroke="white" strokeWidth="2" strokeDasharray="10 20" strokeLinecap="round"/>
                </svg>
                {t('SENDING')}
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M14.6667 2L7.33337 9.33333M14.6667 2L10 14.6667L7.33337 9.33333M14.6667 2L1.33337 6.66667L7.33337 9.33333" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {isEditMode ? t('EDIT_REQUEST_INVOICE') : t('SEND_INVOICE')}
              </>
            )}
          </button>
        </div>
      </div>

      <SendInvoiceConfirmationModal
        show={showConfirmModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmSend}
        onSuccess={handleSuccess}
        isLoading={isLoading}
        isEditMode={isEditMode}
        invoiceAmount={grandTotal}
        invoiceDay={invoiceDay}
        dueDay={dueDay}
        freelancerSubtotals={freelancerSubtotals}
        calculatedSubAmount={calculatedSubAmount}
        selectedCustomer={selectedCustomer}
        customerIdFromQuery={customerIdFromQuery}
        preId={preId}
        dueDateFromEdit={dueDateFromPreferences}
      />

      <BankInfoModal
        show={showBankInfoModal}
        onClose={() => setShowBankInfoModal(false)}
      />
       <SaveInvoiceModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={handleConfirmSave}
        getPayload={getPayload}
        onSecondApiCall={onSecondApiCall}
      />
    </>
  );
};

export default InvoiceFooter;